import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ProductoInternalNoteApiService } from '../services/producto-internal-note-api.service';
import {
  ProductInternalNoteRequest,
  ProductInternalNoteResponse,
  ProductInternalNoteUpdateRequest
} from '../models/producto-internal-note.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';

/**
 * Notas internas de un producto (Incidente #26, INC_CRM_006): listar, crear,
 * editar (solo autor) y eliminar (solo autor). Observaciones administrativas
 * visibles solo para el equipo interno; nunca se muestran al cliente externo.
 * El backend resuelve el tenant y el autor desde el JWT.
 */
@Component({
  selector: 'app-producto-internal-notes',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './producto-internal-notes.component.html'
})
export class ProductoInternalNotesComponent implements OnChanges {
  @Input() productId: number | null = null;

  private readonly MAX_CONTENT = 5000;

  notes: ProductInternalNoteResponse[] = [];
  loading = false;
  loadError = '';

  // Composer (crear o editar; solo uno activo a la vez)
  editingNoteId: number | null = null;
  draftContent = '';
  saving = false;

  deletingIds = new Set<number>();

  constructor(
    private noteApi: ProductoInternalNoteApiService,
    private authService: AuthService,
    private datetime: DatetimeService,
    private toast: ToastService,
    private confirmDialog: ConfirmDialogService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['productId']) {
      this.resetComposer();
      this.notes = [];
      if (this.productId) {
        this.cargar();
      }
    }
  }

  private get currentUserId(): number | null {
    return this.authService.getCurrentUser()?.id ?? null;
  }

  get isEditing(): boolean {
    return this.editingNoteId !== null;
  }

  get contentLength(): number {
    return this.draftContent.length;
  }

  get maxContent(): number {
    return this.MAX_CONTENT;
  }

  private cargar(): void {
    if (!this.productId) return;
    this.loading = true;
    this.loadError = '';
    this.noteApi.getInternalNotes(this.productId).subscribe({
      next: (notes) => {
        this.notes = notes || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.loadError = err.error?.message || 'No se pudieron cargar las notas internas.';
      }
    });
  }

  startEdit(note: ProductInternalNoteResponse): void {
    this.editingNoteId = note.id;
    this.draftContent = note.content;
  }

  cancelEdit(): void {
    this.resetComposer();
  }

  private resetComposer(): void {
    this.editingNoteId = null;
    this.draftContent = '';
  }

  submit(): void {
    if (this.saving) return;
    const content = this.draftContent.trim();
    if (!content) {
      this.toast.error({ title: 'Nota vacía', message: 'Escribe el contenido de la nota.' });
      return;
    }
    if (content.length > this.MAX_CONTENT) {
      this.toast.error({ title: 'Nota muy larga', message: `Máximo ${this.MAX_CONTENT} caracteres.` });
      return;
    }
    if (!this.productId) return;

    this.saving = true;
    const editingId = this.editingNoteId;
    const obs = editingId !== null
      ? this.noteApi.updateInternalNote(this.productId, editingId, { content } as ProductInternalNoteUpdateRequest)
      : this.noteApi.createInternalNote(this.productId, { content } as ProductInternalNoteRequest);

    obs.subscribe({
      next: (saved) => {
        this.saving = false;
        if (editingId !== null) {
          this.notes = this.notes.map(n => (n.id === editingId ? saved : n));
          this.toast.updated('Nota');
        } else {
          this.notes = [...this.notes, saved];
          this.toast.created('Nota');
        }
        this.resetComposer();
      },
      error: (err) => {
        this.saving = false;
        const msg = err?.status === 403
          ? 'Solo el autor puede editar esta nota.'
          : err.error?.message || 'No se pudo guardar la nota.';
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  async eliminar(note: ProductInternalNoteResponse): Promise<void> {
    if (!this.productId || this.deletingIds.has(note.id)) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar nota',
      message: '¿Eliminar esta nota interna? Esta acción no se puede deshacer.'
    });
    if (!ok) return;

    this.deletingIds.add(note.id);
    this.noteApi.deleteInternalNote(this.productId, note.id).subscribe({
      next: () => {
        this.deletingIds.delete(note.id);
        this.notes = this.notes.filter(n => n.id !== note.id);
        if (this.editingNoteId === note.id) this.resetComposer();
        this.toast.deleted('Nota');
      },
      error: (err) => {
        this.deletingIds.delete(note.id);
        const msg = err?.status === 403
          ? 'Solo el autor puede eliminar esta nota.'
          : err.error?.message || 'No se pudo eliminar la nota.';
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  // ---- Presentación ----

  isAuthor(note: ProductInternalNoteResponse): boolean {
    return this.currentUserId !== null && note.userId === this.currentUserId;
  }

  isDeleting(note: ProductInternalNoteResponse): boolean {
    return this.deletingIds.has(note.id);
  }

  wasEdited(note: ProductInternalNoteResponse): boolean {
    if (!note.datetimeUpdated || !note.datetimeCreated) return false;
    // Se considera editada solo si la actualización es claramente posterior a la
    // creación (tolerancia de 2s: al crear, created/updated pueden diferir en microsegundos).
    const created = new Date(note.datetimeCreated).getTime();
    const updated = new Date(note.datetimeUpdated).getTime();
    return updated - created > 2000;
  }

  authorInitials(note: ProductInternalNoteResponse): string {
    const parts = (note.authorName || '?').trim().split(/\s+/);
    const first = parts[0]?.charAt(0) || '';
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase() || '?';
  }

  authorLabel(note: ProductInternalNoteResponse): string {
    return note.authorName || note.authorEmail || `Usuario #${note.userId}`;
  }

  formatDateTime(iso?: string | null): string {
    return iso ? this.datetime.formatDateTimeShort(iso) : '';
  }
}
